<template>
	<div
		ref="obj"
		:class="classes"
		:style="transform"
		@mouseenter="hovering = true"
		@mouseleave="hovering = false"
		@mousedown="handleMousedown"
		@click="handleClick"
	>
		<div class="polaroid__image">
			<Submission v-bind="submission" />
		</div>
		<div class="polaroid__text">
			<p class="polaroid__text__title" v-if="!isText">
				{{ submission.title }}
			</p>
			<p>
				<span class="polaroid__text__extra">submitted </span>by
				{{ name }}
			</p>
		</div>
		<div v-if="first" class="polaroid__position polaroid__position--first">
			<img src="@/assets/crown.svg" alt="First place" />
		</div>
		<div
			v-else-if="numbered"
			class="polaroid__position"
			:style="positionTransform"
		>
			{{ submission.position }}
		</div>
	</div>
</template>

<script>
import { enums } from 'protoswipe-shared'
import Submission from '@/components/shared/Submission'

const ANGLE_MAX = 5

// Helpers
const randBetween = (min, max) => Math.random() * (max - min) + min

export default {
	name: 'ThumbnailPolaroid',
	props: {
		submission: Object,
		numbered: {
			type: Boolean,
			default: false
		},
		large: {
			type: Boolean,
			default: false
		},
		wall: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			hovering: false,
			angle: randBetween(-ANGLE_MAX, ANGLE_MAX)
		}
	},
	computed: {
		classes() {
			return [
				'polaroid',
				{ 'polaroid--large': this.large, 'polaroid--wall': this.wall }
			]
		},
		first() {
			return this.numbered && this.submission.position === 1
		},
		isText() {
			return this.submission.campaign.type === enums.CampaignType.text
		},
		name() {
			return this.submission.nickname || 'Anonymous'
		},
		transform() {
			const scale = this.hovering ? 1.05 : 1
			const angle = this.hovering ? 0 : this.angle
			return {
				transform: `scale(${scale}) rotate(${angle}deg)`
			}
		},
		positionTransform() {
			const angle = this.hovering ? 0 : -this.angle
			return {
				transform: `rotate(${angle}deg)`
			}
		}
	},
	methods: {
		handleClick() {
			if (!this.wall) this.$emit('click', this.submission.id)
		},
		handleMousedown() {
			if (this.wall) this.$emit('click', this.submission.id)
		}
	},
	components: {
		Submission
	}
}
</script>

<style lang="scss" scoped>
@use 'sass:math';
@import '~scss/vars';
@import '~scss/mq';

$ease: 0.15s $ease-out-sine;

@mixin position($size) {
	top: math.div(-$size, 2);
	left: math.div(-$size, 2);
	width: $size;
	height: $size;
}

.polaroid {
	position: relative;
	width: 100%;
	max-width: 20rem;
	padding: 1rem;
	background-color: $white;
	box-shadow: 0 0.2rem 0.25rem rgba($black, 0.3);
	user-select: none;
	cursor: pointer;
	transition: transform $ease, box-shadow $ease;

	&--wall {
		max-width: 15rem;
		padding: 0.75rem;
		p {
			font-size: 70%;
		}
	}

	&--large:not(&--wall) {
		max-width: 24rem;
	}

	&:hover {
		box-shadow: 0 0.25rem 0.75rem 0.25rem rgba($black, 0.1);
	}

	&__image {
		margin-bottom: 1rem;
	}

	&--wall &__image {
		margin-bottom: 0.75rem;
	}

	&__text {
		color: $blue-48;
		text-align: right;
		> p {
			margin: 0;
			line-height: 1.5;
			word-break: break-word;
		}
		&__title {
			font-weight: bold;
		}
		&__extra {
			display: none;
		}
	}

	&__position {
		position: absolute;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.5rem;
		font-weight: bold;
		color: $white;
		transition: transform $ease;

		&--first {
			top: -3.2rem;
			left: -1.8rem;
			width: 4rem;
			height: 4rem;
		}
		&:not(&--first) {
			@include position(3rem);
			border-radius: 50%;
			background-color: $blue-48;
		}
	}

	&--wall &__position {
		font-size: 1.25rem;

		&--first {
			top: -2.85rem;
			left: -1.55rem;
			width: 3.5rem;
			height: 3.5rem;
		}
		&:not(&--first) {
			@include position(2.5rem);
		}
	}

	@include mq(md) {
		&__text__extra {
			display: inline;
		}
	}
}
</style>
